import { useCallback, useMemo, useState } from "react";

import { useGetIdentity } from "@refinedev/core";

import { IUser } from "@/types";
import {
  GET_MY_USER_PREFERENCES_QUERY,
  type GetMyUserPreferencesData,
  UPDATE_MY_USER_PREFERENCE_MUTATION,
  type UpdateMyUserPreferenceData,
  type UpdateMyUserPreferenceInput,
  type UserPreference,
} from "@/utils/GraphQLClient";
import { DEFAULT_WEIGHT_UNIT, type WeightUnit, isWeightUnit } from "@/utils/weight-units";

import { useMutation, useQuery } from "urql";

/** Preference keys as the Engage API exposes them. */
export const USER_PREFERENCE_KEYS = {
  WEIGHT_UNIT: "weight_unit",
} as const;

export type UserPreferenceKey = (typeof USER_PREFERENCE_KEYS)[keyof typeof USER_PREFERENCE_KEYS];

export { DEFAULT_WEIGHT_UNIT, WEIGHT_UNITS, isWeightUnit, type WeightUnit } from "@/utils/weight-units";

export interface UseUserPreferencesResult {
  /** Every preference for the logged-in user, with defaults already applied by the backend */
  preferences: UserPreference[];
  /** Look up one preference by key */
  getPreference: (key: UserPreferenceKey) => UserPreference | undefined;
  /** True during the initial fetch */
  isLoading: boolean;
  /** True during an update call */
  isUpdating: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Store a value. Returns true on success. */
  updatePreference: (key: UserPreferenceKey, value: string) => Promise<boolean>;
}

/**
 * The logged-in user's preferences from Engage. The operations are self-only:
 * they take no user id, so identity is only used to wait for login.
 */
export const useUserPreferences = (): UseUserPreferencesResult => {
  const { data: user, isLoading: isIdentityLoading } = useGetIdentity<IUser>();
  const isLoggedIn = !!user?.id;
  const [isUpdating, setIsUpdating] = useState(false);

  const [queryResult, reexecuteQuery] = useQuery<GetMyUserPreferencesData>({
    query: GET_MY_USER_PREFERENCES_QUERY,
    pause: !isLoggedIn,
  });

  const [, executeMutation] = useMutation<UpdateMyUserPreferenceData, { input: UpdateMyUserPreferenceInput }>(
    UPDATE_MY_USER_PREFERENCE_MUTATION
  );

  const preferences = useMemo(() => queryResult.data?.getMyUserPreferences ?? [], [queryResult.data]);

  const getPreference = useCallback(
    (key: UserPreferenceKey) => preferences.find(preference => preference.key === key),
    [preferences]
  );

  const error = useMemo(() => {
    const err = queryResult.error;
    if (!err) {
      return null;
    }
    return err.graphQLErrors?.[0]?.message ?? err.message ?? "An error occurred";
  }, [queryResult.error]);

  const updatePreference = useCallback(
    async (key: UserPreferenceKey, value: string): Promise<boolean> => {
      if (!isLoggedIn) {
        return false;
      }

      setIsUpdating(true);
      try {
        const result = await executeMutation({ input: { key, value } });
        if (result.error) {
          return false;
        }
        reexecuteQuery({ requestPolicy: "network-only" });
        return true;
      } finally {
        setIsUpdating(false);
      }
    },
    [isLoggedIn, executeMutation, reexecuteQuery]
  );

  return {
    preferences,
    getPreference,
    isLoading: isIdentityLoading || queryResult.fetching,
    isUpdating,
    error,
    updatePreference,
  };
};

export interface UseWeightUnitResult {
  /** Unit to display weights in: the user's choice, else their organization's default */
  weightUnit: WeightUnit;
  isLoading: boolean;
  isUpdating: boolean;
  /** Error message if the preferences fetch failed; weightUnit is then only the fallback */
  error: string | null;
  /** Store the user's unit. Returns true on success. */
  setWeightUnit: (unit: WeightUnit) => Promise<boolean>;
}

/** The logged-in user's weight display unit, from their weight_unit preference. */
export const useWeightUnit = (): UseWeightUnitResult => {
  const { getPreference, isLoading, isUpdating, error, updatePreference } = useUserPreferences();

  const value = getPreference(USER_PREFERENCE_KEYS.WEIGHT_UNIT)?.value;
  const weightUnit = isWeightUnit(value) ? value : DEFAULT_WEIGHT_UNIT;

  const setWeightUnit = useCallback(
    (unit: WeightUnit) => updatePreference(USER_PREFERENCE_KEYS.WEIGHT_UNIT, unit),
    [updatePreference]
  );

  return { weightUnit, isLoading, isUpdating, error, setWeightUnit };
};
